/**
 * Radar Chart Component
 * Gráfico cupping (spider/polar) para notas sensoriais
 */

class RadarChart {
  constructor(element) {
    this.container = element;
    this.svg = element.querySelector('.rd-radar__svg');
    this.tooltip = element.querySelector('.rd-radar__tooltip');
    this.chartId = element.dataset.radarId || 'radar';
    this.data = this.parseData(element.dataset.radarData, element.dataset.radarSource);
    this.size = parseInt(element.dataset.radarSize, 10) || 280;
    this.center = this.size / 2;
    // No mobile o gráfico precisa preencher mais a tela (menos “aro” vazio)
    const isCompact = window.matchMedia('(max-width: 767px)').matches;
    this.maxRadius = this.center * (isCompact ? 0.58 : 0.44);
    this.labelRadius = this.center * (isCompact ? 0.84 : 0.72);
    this.levels = 5;
    this.maxValue = 5;

    if (this.tooltip) {
      this.tooltip.style.display = 'none';
    }

    this.init();
  }

  // Converte dados (originalmente 0-100) para escala 0-5
  parseData(dataString, rawSource) {
    const fromRaw = this.parsePairsFromRaw(rawSource);
    if (fromRaw.length >= 3) return fromRaw;
    return this.parsePairs(dataString);
  }

  parsePairs(dataString) {
    if (!dataString) return [];

    return dataString.split(',').map(pair => {
      const [label, value] = pair.split(':').map(s => s.trim());
      const numValue = parseFloat(value);
      const scaledValue = (numValue / 100) * 5;
      return {
        label: label,
        value: Math.min(5, Math.max(0, scaledValue)),
        originalValue: numValue
      };
    }).filter(item => item.label && !isNaN(item.value));
  }

  parsePairsFromRaw(raw) {
    if (!raw) return [];

    const reserved = /^(headline|texto|texto2|text|cta|cta_label|botao|botão|chart|gráfico|grafico)$/i;
    const pairs = [];
    const re = /([^§\n:]+):\s*(\d{1,3})(?!\d)/g;
    let match;

    while ((match = re.exec(raw)) !== null) {
      const label = match[1].trim();
      if (!label || reserved.test(label)) continue;
      const numValue = parseFloat(match[2]);
      if (Number.isNaN(numValue)) continue;
      const scaledValue = (numValue / 100) * 5;
      pairs.push({
        label,
        value: Math.min(5, Math.max(0, scaledValue)),
        originalValue: numValue
      });
    }

    return pairs;
  }

  init() {
    if (this.data.length < 3) {
      console.warn('Radar chart requires at least 3 data points');
      return;
    }

    this.drawGrid();
    this.drawAxes();
    this.drawAxisLabels();
    this.drawData();
    this.drawValueLabels();
    this.drawLabels();
    this.setupIntersectionObserver();
  }

  getCoordinates(index, value) {
    const angleStep = (2 * Math.PI) / this.data.length;
    const angle = angleStep * index - Math.PI / 2;
    const radius = (this.maxRadius * value) / this.maxValue;

    return {
      x: this.center + radius * Math.cos(angle),
      y: this.center + radius * Math.sin(angle),
      angle: angle
    };
  }

  polarToCartesian(radius, angle) {
    return {
      x: this.center + radius * Math.cos(angle),
      y: this.center + radius * Math.sin(angle)
    };
  }

  describeArc(radius, startAngle, endAngle, reverse) {
    const start = this.polarToCartesian(radius, reverse ? endAngle : startAngle);
    const end = this.polarToCartesian(radius, reverse ? startAngle : endAngle);
    const delta = Math.abs(endAngle - startAngle);
    const largeArc = delta > Math.PI ? 1 : 0;
    // sweep-flag: 1 = clockwise in SVG screen coords when going start→end
    const sweep = reverse ? 0 : 1;

    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} ${sweep} ${end.x} ${end.y}`;
  }

  drawGrid() {
    const gridGroup = this.svg.querySelector('.rd-radar__grid');

    for (let level = 1; level <= this.levels; level++) {
      const radius = (this.maxRadius * level) / this.levels;
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.classList.add('rd-radar__grid-level');
      circle.setAttribute('cx', this.center);
      circle.setAttribute('cy', this.center);
      circle.setAttribute('r', radius);
      circle.setAttribute('data-level', level);
      gridGroup.appendChild(circle);
    }
  }

  drawAxes() {
    const axesGroup = this.svg.querySelector('.rd-radar__axes');

    this.data.forEach((_, i) => {
      const end = this.getCoordinates(i, this.maxValue);
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.classList.add('rd-radar__axis');
      line.setAttribute('x1', this.center);
      line.setAttribute('y1', this.center);
      line.setAttribute('x2', end.x);
      line.setAttribute('y2', end.y);
      axesGroup.appendChild(line);
    });
  }

  // Marcações 1–5 nas cruzes N / E / S / W (como cupping chart)
  drawAxisLabels() {
    const axesGroup = this.svg.querySelector('.rd-radar__axes');
    // Ângulos: topo, direita, baixo, esquerda
    const crossAngles = [-Math.PI / 2, 0, Math.PI / 2, Math.PI];

    crossAngles.forEach((angle, axisIdx) => {
      for (let level = 1; level <= this.levels; level++) {
        const radius = (this.maxRadius * level) / this.levels;
        const coords = this.polarToCartesian(radius, angle);

        // Offset leve para não colidir com a linha do eixo
        let ox = 0;
        let oy = 0;
        if (axisIdx === 0) oy = -7; // topo
        if (axisIdx === 1) ox = 8;  // direita
        if (axisIdx === 2) oy = 12; // baixo
        if (axisIdx === 3) ox = -8; // esquerda

        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        text.classList.add('rd-radar__axis-label');
        text.setAttribute('x', coords.x + ox);
        text.setAttribute('y', coords.y + oy);
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('dominant-baseline', 'middle');
        text.textContent = level;
        axesGroup.appendChild(text);
      }
    });
  }

  drawData() {
    const dataGroup = this.svg.querySelector('.rd-radar__data');

    const points = this.data.map((item, i) => {
      const coords = this.getCoordinates(i, item.value);
      return `${coords.x},${coords.y}`;
    }).join(' ');

    const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    polygon.classList.add('rd-radar__polygon');
    polygon.setAttribute('points', points);
    polygon.setAttribute('data-rd-radar-polygon', '');
    dataGroup.appendChild(polygon);

    this.data.forEach((item, i) => {
      const coords = this.getCoordinates(i, item.value);

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.classList.add('rd-radar__point');
      circle.setAttribute('cx', coords.x);
      circle.setAttribute('cy', coords.y);
      circle.setAttribute('r', 2.5);
      circle.setAttribute('data-label', item.label);
      circle.setAttribute('data-value', item.value);

      circle.addEventListener('mouseenter', (e) => this.showTooltip(e, item));
      circle.addEventListener('mousemove', (e) => this.updateTooltipPosition(e));
      circle.addEventListener('mouseleave', () => this.hideTooltip());

      dataGroup.appendChild(circle);
    });
  }

  drawValueLabels() {
    const dataGroup = this.svg.querySelector('.rd-radar__data');

    this.data.forEach((item, i) => {
      const coords = this.getCoordinates(i, item.value);
      const angleStep = (2 * Math.PI) / this.data.length;
      const angle = angleStep * i - Math.PI / 2;

      const offsetDistance = 14;
      const offsetX = Math.cos(angle) * offsetDistance;
      const offsetY = Math.sin(angle) * offsetDistance;

      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.classList.add('rd-radar__value-label');
      text.setAttribute('x', coords.x + offsetX);
      text.setAttribute('y', coords.y + offsetY);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'middle');
      text.textContent = item.value.toFixed(1);

      dataGroup.appendChild(text);
    });
  }

  // Labels curvados com textPath (metade inferior invertida, estilo cupping)
  drawLabels() {
    const labelsGroup = this.svg.querySelector('.rd-radar__labels');
    let pathsGroup = this.svg.querySelector('.rd-radar__label-paths');

    if (!pathsGroup) {
      pathsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      pathsGroup.classList.add('rd-radar__label-paths');
      this.svg.insertBefore(pathsGroup, labelsGroup);
    }

    const n = this.data.length;
    const angleStep = (2 * Math.PI) / n;
    // Largura angular do arco de cada label (~70% do setor)
    const halfArc = angleStep * 0.38;

    this.data.forEach((item, i) => {
      const midAngle = angleStep * i - Math.PI / 2;
      // Metade inferior do círculo: texto segue o arco “de cabeça pra baixo”
      const isBottom = Math.sin(midAngle) > 0.15;
      const pathId = `${this.chartId}-label-path-${i}`;

      const startAngle = midAngle - halfArc;
      const endAngle = midAngle + halfArc;
      const d = this.describeArc(this.labelRadius, startAngle, endAngle, isBottom);

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('id', pathId);
      path.setAttribute('d', d);
      path.setAttribute('fill', 'none');
      path.classList.add('rd-radar__label-path');
      pathsGroup.appendChild(path);

      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.classList.add('rd-radar__label');

      const textPath = document.createElementNS('http://www.w3.org/2000/svg', 'textPath');
      textPath.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `#${pathId}`);
      textPath.setAttribute('href', `#${pathId}`);
      textPath.setAttribute('startOffset', '50%');
      textPath.setAttribute('text-anchor', 'middle');
      textPath.textContent = item.label.toUpperCase();

      text.appendChild(textPath);
      labelsGroup.appendChild(text);
    });
  }

  setupIntersectionObserver() {
    if (!window.IntersectionObserver || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.showElements();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.animateIn();
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.45,
      rootMargin: '0px 0px -18% 0px'
    });

    observer.observe(this.container);
  }

  animateIn() {
    const polygon = this.svg.querySelector('.rd-radar__polygon');
    const points = this.svg.querySelectorAll('.rd-radar__point');
    const labels = this.svg.querySelectorAll('.rd-radar__label');
    const valueLabels = this.svg.querySelectorAll('.rd-radar__value-label');

    // Delay inicial para o usuário já estar olhando antes do “draw”
    const startDelay = 280;

    labels.forEach((label, i) => {
      setTimeout(() => label.classList.add('is-visible'), startDelay + i * 90);
    });

    setTimeout(() => {
      if (polygon) polygon.setAttribute('data-animated', 'true');
    }, startDelay + 220);

    points.forEach((point, i) => {
      setTimeout(() => point.classList.add('is-visible'), startDelay + 700 + i * 100);
    });

    valueLabels.forEach((label, i) => {
      setTimeout(() => label.classList.add('is-visible'), startDelay + 950 + i * 100);
    });
  }

  showElements() {
    const polygon = this.svg.querySelector('.rd-radar__polygon');
    const points = this.svg.querySelectorAll('.rd-radar__point');
    const labels = this.svg.querySelectorAll('.rd-radar__label');
    const valueLabels = this.svg.querySelectorAll('.rd-radar__value-label');

    if (polygon) polygon.style.opacity = '1';
    points.forEach(point => point.classList.add('is-visible'));
    labels.forEach(label => label.classList.add('is-visible'));
    valueLabels.forEach(label => label.classList.add('is-visible'));
  }

  showTooltip(event, item) {
    if (!this.tooltip) return;

    const labelEl = this.tooltip.querySelector('.rd-radar__tooltip-label');
    const valueEl = this.tooltip.querySelector('.rd-radar__tooltip-value');

    if (labelEl) labelEl.textContent = item.label + ':';
    if (valueEl) valueEl.textContent = item.value.toFixed(1);

    this.tooltip.style.display = 'block';
    this.updateTooltipPosition(event);
  }

  updateTooltipPosition(event) {
    if (!this.tooltip || this.tooltip.style.display === 'none') return;

    const containerRect = this.container.getBoundingClientRect();
    const tooltipRect = this.tooltip.getBoundingClientRect();

    const x = event.clientX - containerRect.left - tooltipRect.width / 2;
    const y = event.clientY - containerRect.top - tooltipRect.height - 12;

    this.tooltip.style.left = `${x}px`;
    this.tooltip.style.top = `${y}px`;
  }

  hideTooltip() {
    if (!this.tooltip) return;
    this.tooltip.style.display = 'none';
  }
}

export function initRadarCharts() {
  const charts = document.querySelectorAll('[data-rd-radar]');

  charts.forEach(chart => {
    try {
      new RadarChart(chart);
    } catch (error) {
      console.error('Error initializing radar chart:', error);
    }
  });
}

if (typeof module === 'undefined' && document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRadarCharts);
} else if (typeof module === 'undefined') {
  initRadarCharts();
}
